import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnInit,
  ViewChild,
  NgZone,
  Renderer2,
} from '@angular/core';
import { LMFormComponent } from '@form/lm-form.component';
import { LMRouteComponentService } from '@services/lm-route-component.service';
import { LMModel } from '@form/model';
import { LMGlobalPersonalizationService } from '@app/states';

@Component({
  selector: 'lm-compact-form',
  template: `
    <lm-form #compactform
      *ngIf="formSpec"
      [formUrlInfo]="formUrlInfo"
      [formSpec]="formSpec"
      [initDataView]="dataView"
      [formOptions]="formOptions"
      [specFields]="specFields"
      [ngStyle]="{'height': '100%'}"
      (formDisplayed)="onFormDisplayed($event)"
    >
    </lm-form>
  `,
  providers: [ LMRouteComponentService ]
})

export class LMCompactFormDialogComponent implements OnInit, AfterViewInit {
  @Input() formUrlInfo: LMFormUrlInfo;
  @Input() formSpec: LMFormSpec;
  @Input() dataView: LMDataView;
  @Input() formOptions: LMFormOptions;
  @Input() specFields: LMSpecFields;
  @Input() parentModel: LMModel;
  @Input() buttonIds: {ok: string, clear: string};
  @Input() defaultButton: SohoModalButton;
  private isInAccessibilityMode: boolean;

  @ViewChild(forwardRef(() => LMFormComponent)) formComponent; // : LMFormComponent;

  @HostListener('keyup', [ '$event' ]) onKeyUp(event) {
    if (event.which === 13 && event.srcElement.nodeName !== 'BUTTON') {
      event.stopPropagation();
      event.stopImmediatePropagation();
      event.preventDefault();

      this.elementRef.nativeElement.parentElement.parentElement.parentElement.parentNode.querySelector('button[id="' + this.buttonIds.ok + '"]').click();
    }
  }

  constructor(
    private elementRef: ElementRef,
    private ngZone: NgZone,
    private renderer: Renderer2,
    private globalPersonalizationService: LMGlobalPersonalizationService
  ) {}

  ngOnInit() {
    this.isInAccessibilityMode = this.globalPersonalizationService.getAccessibilityMode();
  }

  ngAfterViewInit() {

    // When dataview is set in the template, the form component
    // is not fully intitialized and model is null.
    // This causes empty form to come up in the required response dialog.
    // Moving set dataview to ngAfterViewInit solves this issue
    // Commenting it out as Vonny moved setData from _initDataView setter to formDataLoaded - 09/19/17

    // this.formComponent.initDataView = this.dataView;
    const okButton: Element = this.elementRef.nativeElement.parentElement.parentElement.parentElement.querySelector(`button[id=${this.buttonIds.ok}]`);
    okButton.addEventListener('keydown', (event: KeyboardEvent) => {
      if(event.keyCode === 9 && event.shiftKey) {
        okButton.setAttribute('data-shift-tabbed', String(true));
      } else {
        okButton.removeAttribute('data-shift-tabbed');
      }
    });
    okButton.addEventListener('focusout', (event: FocusEvent) => {
      const elementToFocus: Element = event?.relatedTarget as Element;
      if (okButton.getAttribute('data-shift-tabbed') === 'true') {
        if ((elementToFocus?.closest('lm-date')
          || elementToFocus?.closest('lm-time')
          || elementToFocus?.closest('lm-lookup'))) {
          //LMCLIENT-36048 when shift tabbing to a field with inline button, it should skip the inline button
          //and should focus to the input element.
          elementToFocus?.closest('lm-field').querySelector('input').focus();
        }
        okButton.removeAttribute('data-shift-tabbed');
      }
    });

    if (this.formSpec.selectViewType !== 'list') {
      return;
    }

    //ok buttons are not tabbable when opened from lists.
    if (!this.isInAccessibilityMode) {
      this.renderer.setAttribute(okButton, 'tabIndex', '-1');
    }
    if (!this.isInAccessibilityMode) {
      const selector = [
        'button:not([disabled]):not([tabindex="-1"])',
        //      '[href]:not([disabled]):not([tabindex="-1"])', //excluding links
        'input:not([disabled]):not([tabindex="-1"])',
        'div.dropdown:not(.is-disabled):not([tabindex="-1"])',
        'div.multiselect:not(.is-disabled):not([tabindex="-1"])',
        'textarea:not([readonly]):not([disabled]):not([tabindex="-1"])',
        '[focusable]:not([focusable="false"])',
        '[tabindex]:not([tabindex="-1"])',
        '[contenteditable="true"]',
      ];
      const focusableEls = this.elementRef.nativeElement.querySelectorAll(selector.join(','));
      const lastFocusableEl = focusableEls[focusableEls.length - 1];

      // Using keydown alone to handle requirement for LMCLIENT-35630 causes the focus to be out of order
      // not sure why
      let tabPressed = false;
      lastFocusableEl.addEventListener('keydown', (e) => {
        if (e.keyCode === 9 && e.shiftKey !== true) {
          tabPressed = true;
        }
      });

      lastFocusableEl.addEventListener('focusout', (e) => {
        if (tabPressed) {
          this.defaultButton.click(e, null);
        }
      });
    }
  }

  onFormDisplayed(event) {
    setTimeout(() => {
      if (this.parentModel) {
        this.formComponent.model.applyState(this.parentModel);
      }
    }, 1);
  }

  public clearForm(dataView: LMDataView) {
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.ngZone.run(() => {
          this.formComponent.model.setData(dataView);
        });
      });
    });
  }
}
